import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Rating,
  IconButton,
  CircularProgress,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  TextField,
  Avatar,
  useMediaQuery,
  useTheme,
  Divider,
  Drawer,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import CloseIcon from '@mui/icons-material/Close';
import NavbarBack from './NavbarBack';
import FeedbackSection from './FeedbackSection';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [currentImg, setCurrentImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openVideo, setOpenVideo] = useState(false);
  const [commentBoxOpen, setCommentBoxOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [replyBoxOpen, setReplyBoxOpen] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [likedComments, setLikedComments] = useState({});
     const [hoveredThumbnail, setHoveredThumbnail] = useState(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        const response = await fetch(`https://reviewhub-lc.onrender.com/api/products/${id}`);
        const data = await response.json();
        setProduct(data);
      } catch (error) {
        console.error('Error fetching product:', error);
      }
    };

    fetchData();
  }, [id]);

  const formatLikes = (num) => {
    if (num >= 1_000_000) {
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1_000) {
      return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString();
  };

  useEffect(() => {
    setLoading(true);
    fetch(`https://reviewhub-lc.onrender.com/api/reviews/${id}`)
      .then(res => res.json())
      .then(data => {
        setProduct(data);
        setComments(data.comments || []);
        setLoading(false);

        const storedLikes = localStorage.getItem(`likedComments_${id}`);
        if (storedLikes) setLikedComments(JSON.parse(storedLikes));
        else setLikedComments({});
      })
      .catch(err => {
        console.error('Error fetching product:', err);
        setLoading(false);
      });
  }, [id]);

  if (loading) return <CircularProgress />;
  if (!product) return <Typography>Product not found.</Typography>;

  const images = product.images || [];

// Inside ProductDetail component

// Combine images + video into one array
const mediaItems = [
  ...(product.images || []).map(img => ({ type: 'image', src: img })),
  ...(product.video ? [{ type: 'video', src: product.video }] : [])
];

const handleNext = () => {
  setCurrentImg((prev) => (prev + 1) % mediaItems.length);
};

const handlePrev = () => {
  setCurrentImg((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
};


  const handleAddComment = async () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const username = localStorage.getItem('username');

    if (!isLoggedIn || !username) {
      alert('You must be logged in to comment');
      setCommentBoxOpen(false);
      return;
    }

    if (!commentText.trim()) return;

    try {
      const res = await fetch(`https://reviewhub-lc.onrender.com/api/reviews/${id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, text: commentText }),
      });

      const data = await res.json();
      setComments(data.comments);
      setCommentText('');
      if (isMobile) setCommentBoxOpen(false);
    } catch (err) {
      console.error('Failed to submit comment', err);
    }
  };

  const getUsernameColor = (username) => {
    const colors = ['#FF5252', '#FF4081', '#E040FB', '#7C4DFF', '#536DFE', '#448AFF', '#40C4FF', '#18FFFF', '#64FFDA', '#69F0AE', '#B2FF59', '#EEFF41', '#FFFF00', '#FFD740', '#FFAB40', '#FF6E40'];
    const hash = username.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    return colors[hash % colors.length];
  };

  const isReseller = localStorage.getItem('loginType') === 'reseller';
  const resellerId = localStorage.getItem('resellerId');
  const resellerImage = localStorage.getItem('resellerImage') || '';
  const resellerName = localStorage.getItem('resellerName') || '';

  const isProductOwner = isReseller && resellerId === product.reviewerId;

  const handleAddReply = async (commentIdx) => {
    if (!isProductOwner) {
      alert("Only the reseller of this product can reply to comments.");
      return;
    }

    const username = resellerName;
    const profileImage = resellerImage;
    const resellerId = localStorage.getItem('resellerId');


    if (!replyText.trim()) return;

    try {
      const res = await fetch(`https://reviewhub-lc.onrender.com/api/reviews/${id}/comment/${commentIdx}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          text: replyText,
          profileImage,
          resellerId,
        }),
      });

      const data = await res.json();
      setComments(data.comments);
      setReplyBoxOpen(null);
      setReplyText('');
    } catch (err) {
      alert('Failed to send reply');
    }
  };

  const handleToggleLikeComment = async (commentCreatedAt) => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const username = localStorage.getItem('username');
 

    if (!isLoggedIn || !username) {
      alert('You must be logged in to like comments.');
      return;
    }

    const commentIdx = comments.findIndex(c => c.createdAt === commentCreatedAt);
    if (commentIdx === -1) return;

    try {
      const res = await fetch(`https://reviewhub-lc.onrender.com/api/reviews/${id}/comment/${commentIdx}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });

      const data = await res.json();
      setComments(data.comments);

      const updatedLikes = { ...likedComments };
      if (updatedLikes[commentCreatedAt]) {
        delete updatedLikes[commentCreatedAt];
      } else {
        updatedLikes[commentCreatedAt] = true;
      }
      setLikedComments(updatedLikes);
      localStorage.setItem(`likedComments_${id}`, JSON.stringify(updatedLikes));
    } catch (err) {
      alert('Failed to toggle like');
    }
  };

  const formatDateTime = (datetimeStr) => {
    if (!datetimeStr) return '';
    const date = new Date(datetimeStr);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderCommentsSection = () => (
    <Box sx={{ 
      width: isMobile ? '100%' : 600, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderLeft: isMobile ? 'none' : '1px solid #e0e0e0',
    }}>
      <Box
  sx={{
    p: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #e0e0e0',
  }}
>
  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
    <ChatBubbleOutlineIcon sx={{ mr: 1 }} />
    {comments.length} Comments
  </Typography>
  <IconButton onClick={() => setCommentBoxOpen(false)}>
    <CloseIcon />
  </IconButton>
</Box>


      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto',
        p: 2
      }}>
        {comments.length === 0 ? (
          <Typography sx={{ textAlign: 'center', py: 4 }}>
            No comments yet. Be the first to comment!
          </Typography>
        ) : (
          [...comments]
            .sort((a, b) => (b.likes || 0) - (a.likes || 0))
            .map((c) => (
              <Box key={c.createdAt} sx={{ display: 'flex', mb: 2, alignItems: 'flex-start' }}>
                <Avatar
                  sx={{
                    bgcolor: getUsernameColor(c.username),
                    fontWeight: 'bold',
                    width: 40,
                    height: 40,
                    fontSize: 18,
                  }}
                  alt={c.username}
                >
                  {c.username[0]?.toUpperCase()}
                </Avatar>

                <Box
                  sx={{
                    ml: 2,
                    borderRadius: 2,
                    bgcolor: '#F7F7F7',
                    p: 2,
                    width: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{ fontWeight: 'bold', fontSize: 16, textTransform: 'capitalize' }}
                    >
                      {c.username}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                    >
                      {formatDateTime(c.createdAt)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, mt: 0.5 }}>{c.text}</Typography>

                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      {formatLikes(c.likes || 0)} {c.likes === 1 ? 'Like' : 'Likes'}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => handleToggleLikeComment(c.createdAt)}
                      color={likedComments[c.createdAt] ? 'primary' : 'default'}
                      aria-label={likedComments[c.createdAt] ? 'Unlike' : 'Like'}
                    >
                      {likedComments[c.createdAt] ? <ThumbUpAltIcon /> : <ThumbUpAltOutlinedIcon />}
                    </IconButton>

                    {isProductOwner && (
                      <Button
                        size="small"
                        onClick={() => setReplyBoxOpen(replyBoxOpen === c.createdAt ? null : c.createdAt)}
                      >
                        Reply
                      </Button>
                    )}
                  </Box>

                  {c.replies.map((r, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, mt: 1 }}>
                      <Avatar
                        src={r.profileImage}
                        alt={r.username}
                        sx={{ width: 30, height: 30, mr: 1 }}
                      />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            sx={{
                              fontWeight: 'bold',
                              fontSize: 14,
                              textTransform: 'capitalize',
                            }}
                          >
                            {r.resellerId ? r.resellerId : r.username}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatDateTime(r.createdAt)}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 14 }}>{r.text}</Typography>
                      </Box>
                    </Box>
                  ))}

                  {replyBoxOpen === c.createdAt && (
                    <Box sx={{ mt: 1, ml: 3 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        fullWidth
                        multiline
                        maxRows={3}
                        placeholder="Write a reply..."
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => handleAddReply(comments.findIndex(x => x.createdAt === c.createdAt))}
                        disabled={!replyText.trim()}
                      >
                        Send Reply
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            ))
        )}
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <TextField
          variant="outlined"
          fullWidth
          multiline
          maxRows={4}
          placeholder="Add a comment..."
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          sx={{ mb: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleAddComment}
          disabled={!commentText.trim()}
          fullWidth
        >
          Send
        </Button>
      </Box>
    </Box>
  );
  

  return (
    <>
      <NavbarBack title="ProductDetails"/>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        height: isMobile ? 'auto' : 'calc(100vh - 64px)',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          p: 3
        }}>
         <Typography
  variant="h4"
  gutterBottom
  sx={{
    fontSize: {
      xs: '1.5rem', // Mobile (extra small screens)
      sm: '2rem',   // Small screens
      md: '2.25rem' // Medium and above (matches h4 size)
    }
  }}
>
  {product.productName}
</Typography>



        <Box sx={{ position: 'relative', width: '100%', maxWidth: 400, margin: '0 auto' }}>
  {/* Main Media Display */}
  <Box sx={{ 
    width: '95%', 
    aspectRatio: '1 / 1', 
    borderRadius: 5, 
    overflow: 'hidden',
    mb: 2
  }}>
    {mediaItems[currentImg].type === 'image' ? (
      <img
        src={`https://reviewhub-lc.onrender.com/${mediaItems[currentImg].src}`}
        alt={`Product media ${currentImg + 1}`}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : (
      <video
        controls
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      >
        <source src={`https://reviewhub-lc.onrender.com/${mediaItems[currentImg].src}`} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    )}
  </Box>

  {/* Navigation Arrows */}
  {/* Navigation Arrows (Only for Desktop) */}
  {!isMobile && mediaItems.length > 1 && (
    <>
      <IconButton
        onClick={handlePrev}
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.4)',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' }
        }}
      >
        <ArrowBackIosNewIcon />
      </IconButton>
      <IconButton
        onClick={handleNext}
        sx={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.4)',
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.6)' }
        }}
      >
        <ArrowForwardIosIcon />
      </IconButton>
    </>
  )}


  {/* Thumbnail Navigation */}
  {mediaItems.length > 1 && (
    <Box sx={{ 
      display: 'flex', 
      gap: 1, 
      overflowX: 'auto',
      py: 1,
      '&::-webkit-scrollbar': { height: 4 },
      '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 2 }
    }}>
      {mediaItems.map((item, index) => (
        <Box
          key={index}
          onClick={() => setCurrentImg(index)}
          onMouseEnter={() => setHoveredThumbnail(index)}
          onMouseLeave={() => setHoveredThumbnail(null)}
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            overflow: 'hidden',
            cursor: 'pointer',
            position: 'relative',
            border: currentImg === index ? '2px solid primary.main' : '1px solid #e0e0e0',
            opacity: hoveredThumbnail === index || currentImg === index ? 1 : 0.8,
            transition: 'all 0.2s ease',
            flexShrink: 0
          }}
        >
          {item.type === 'image' ? (
            <img
              src={`https://reviewhub-lc.onrender.com/${item.src}`}
              alt={`Thumbnail ${index + 1}`}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                filter: hoveredThumbnail === index ? 'brightness(1.1)' : 'none'
              }}
            />
          ) : (
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5'
            }}>
              <PlayCircleOutlineIcon sx={{ color: 'text.secondary' }} />
            </Box>
          )}
          {currentImg === index && (
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 3,
              backgroundColor: 'primary.main'
            }} />
          )}
        </Box>
      ))}
    </Box>
  )}
</Box>

            <Grid item xs={12} md={6}>
              <strong>Rating:</strong>
              <Rating value={product.rating} readOnly sx={{ mt: 2 }} />
              <Typography sx={{ mt: 2 }}><strong>Description:</strong> {product.description}</Typography>
              <Typography sx={{ mt: 2 }}><strong>Category:</strong> {product.category} ({product.subCategory})</Typography>
              <Typography sx={{ mt: 2 }}><strong>Gender:</strong> {product.gender}</Typography>
              <Typography sx={{ mt: 2 }}><strong>Reviewer:</strong> {product.reviewerId}</Typography>
{/* 
              {product.video && (
                <>
                  <Button variant="contained" onClick={() => setOpenVideo(true)} sx={{ mt: 3 }}>
                    Watch Video
                  </Button>
                  <Dialog open={openVideo} onClose={() => setOpenVideo(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Product Video</DialogTitle>
                    <DialogContent>
                      <video controls style={{ width: '100%', borderRadius: 8, height: '500px' }}>
                        <source src={`https://reviewhub-lc.onrender.com/${product.video}`} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setOpenVideo(false)}>Close</Button>
                    </DialogActions>
                  </Dialog>
                </>
              )} */}

              <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
  <Typography fontWeight="bold">Product Link:</Typography>
  <Button
    variant="contained"
    color="primary"
    size="small"
    href={product.link}
    target="_blank"
    rel="noopener noreferrer"
  >
    View Product
  </Button>
</Box>

             <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <IconButton onClick={() => setCommentBoxOpen(true)} sx={{ color: 'primary.main' }}>
      <ChatBubbleOutlineIcon />
    </IconButton>
    <Typography variant="body2" sx={{ ml: 1 }}>
      {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
    </Typography>
  </Box>
 <Typography
  variant="caption"
  color="text.secondary"
  sx={{
    mt: { xs: 0.5, sm: 0 },
    ml: { xs: 0, sm: 1 },
  }}
>
  Ask your questions in this Comment section <br />
  The reviewer will reply to your comments
</Typography>

</Box>

              </Box>
            </Grid>
            <FeedbackSection productId={id} />

        </Box>

        {/* Desktop Comments Sidebar */}
        {!isMobile && commentBoxOpen && renderCommentsSection()}

        {/* Mobile Comments Drawer */}
        {isMobile && (
          <Drawer
            anchor="bottom"
            open={commentBoxOpen}
            onClose={() => setCommentBoxOpen(false)}
            sx={{
              '& .MuiDrawer-paper': {
                height: '80vh',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            }}
          >
            {renderCommentsSection()}
          </Drawer>
        )}
      </Box>
    </>
  );
};

export default ProductDetail;